import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, HandHeart, GraduationCap, Users, Heart, Stethoscope, BookOpen } from 'lucide-react';
import { SEO } from '../components/layout/SEO';
import { Section } from '../components/ui/Section';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FadeIn } from '../components/ui/FadeIn';
import { useLanguage } from '../context/LanguageContext';
export function HomePage() {
  const {
    t,
    isRTL
  } = useLanguage();
  return <>
      <SEO title={t('nav.home')} description={t('home.hero.description')} data-id="element-400" />

      {/* Hero Section */}
      <div className="relative bg-gray-900 text-white overflow-hidden min-h-[65vh] md:min-h-[70vh] flex items-center" data-id="element-401">
        <div className="absolute inset-0" data-id="element-402">
          <img src="https://cdn.magicpatterns.com/uploads/rgrSJawmhW3zF59H7uaMNf/Khabar_medical_college.jpg" alt="Khyber Medical College campus" className="h-full w-full object-cover object-center" data-id="element-403" />
          <div className="absolute inset-0 bg-black/25" data-id="element-404" />
        </div>

        <div className="relative container mx-auto px-4 md:px-6 py-16 md:py-20" data-id="element-405">
          <FadeIn className="max-w-4xl mx-auto" data-id="element-406">
            {/* Glassmorphic Content Card */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 md:p-8 shadow-2xl" data-id="element-407">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-serif font-bold leading-tight mb-4 text-white" style={{
              textShadow: '0 4px 12px rgba(0, 0, 0, 0.25)'
            }} data-id="element-408">
                {t('home.hero.title')}
                <br data-id="element-409" />
                {t('home.hero.subtitle')}
              </h1>
              <p className="text-base md:text-lg text-white/95 leading-relaxed" style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
            }} data-id="element-410">
                {t('home.hero.description')}
              </p>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Who We Are Section */}
      <Section background="white" className="py-10 md:py-12" data-id="element-411">
        <div className="max-w-4xl mx-auto" data-id="element-412">
          <FadeIn className="text-center mb-8" data-id="element-413">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4 text-primary-900" data-id="element-414">
              {t('home.whoWeAre.title')}
            </h2>
            <div className="text-gray-600 leading-relaxed space-y-3 text-base md:text-lg" data-id="element-415">
              <p data-id="element-416">{t('home.whoWeAre.p1')}</p>
              <p data-id="element-417">{t('home.whoWeAre.p2')}</p>
              <p data-id="element-418">
                Through our social connections, cultural values, and charitable
                efforts, we are bringing ourselves closer to the community and
                supporting those who need it most.
              </p>
            </div>
          </FadeIn>

          {/* Important Statement */}
          <FadeIn delay={0.2} data-id="element-419">
            <div className="bg-accent-50 border-l-4 border-accent-600 p-5 rounded-r-lg mt-6" data-id="element-420">
              <p className="text-gray-800 font-medium text-center text-sm md:text-base" data-id="element-421">
                {t('home.whoWeAre.statement')}
              </p>
            </div>
          </FadeIn>
        </div>
      </Section>

      {/* What We Offer Section */}
      <Section background="gray" className="py-10 md:py-12" data-id="element-422">
        <div className="text-center max-w-3xl mx-auto mb-8" data-id="element-423">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-primary-900" data-id="element-424">
            {t('home.whatWeOffer.title')}
          </h2>
          <p className="text-gray-600" data-id="element-425">{t('home.whatWeOffer.subtitle')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-426">
          {/* 1. Social and Cultural Networking */}
          <FadeIn delay={0} data-id="element-427">
            <Link to="/events" className="group" data-id="element-428">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-accent-200" data-id="element-429">
                <CardHeader className="text-center pb-4" data-id="element-430">
                  <div className="h-16 w-16 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-431">
                    <Users className="h-8 w-8" data-id="element-432" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-433">
                    {t('home.whatWeOffer.social.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-434">
                  <CardDescription className="text-base mb-4" data-id="element-435">
                    {t('home.whatWeOffer.social.description')}
                  </CardDescription>
                  <div className="text-accent-700 font-medium group-hover:underline inline-flex items-center" data-id="element-436">
                    {t('common.seeEvents')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-437" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          {/* 2. Membership Benefits */}
          <FadeIn delay={0.1} data-id="element-438">
            <Link to="/membership" className="group" data-id="element-439">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-primary-200" data-id="element-440">
                <CardHeader className="text-center pb-4" data-id="element-441">
                  <div className="h-16 w-16 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-442">
                    <GraduationCap className="h-8 w-8" data-id="element-443" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-444">
                    {t('home.whatWeOffer.membership.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-445">
                  <CardDescription className="text-base mb-4" data-id="element-446">
                    {t('home.whatWeOffer.membership.description')}
                  </CardDescription>
                  <div className="text-primary-700 font-medium group-hover:underline inline-flex items-center" data-id="element-447">
                    {t('common.learnMore')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-448" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>

          {/* 3. Charity Projects */}
          <FadeIn delay={0.2} data-id="element-449">
            <Link to="/charity" className="group" data-id="element-450">
              <Card className="h-full hover:shadow-xl transition-all duration-300 border-2 border-transparent hover:border-green-200" data-id="element-451">
                <CardHeader className="text-center pb-4" data-id="element-452">
                  <div className="h-16 w-16 rounded-full bg-green-100 text-green-700 flex items-center justify-center mb-4 mx-auto group-hover:scale-110 transition-transform" data-id="element-453">
                    <HandHeart className="h-8 w-8" data-id="element-454" />
                  </div>
                  <CardTitle className="text-2xl" data-id="element-455">
                    {t('home.whatWeOffer.charity.title')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center" data-id="element-456">
                  <CardDescription className="text-base mb-4" data-id="element-457">
                    {t('home.whatWeOffer.charity.description')}
                  </CardDescription>
                  <div className="text-green-700 font-medium group-hover:underline inline-flex items-center" data-id="element-458">
                    {t('common.viewProjects')}{' '}
                    <ArrowRight className={`h-4 w-4 group-hover:translate-x-1 transition-transform ${isRTL ? 'mr-2' : 'ml-2'}`} data-id="element-459" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          </FadeIn>
        </div>
      </Section>

      {/* Our Latest Plans Section */}
      <Section className="py-10 md:py-12" data-id="element-460">
        <div className="text-center max-w-3xl mx-auto mb-10" data-id="element-461">
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-3 text-primary-900" data-id="element-462">
            Our Latest Plans
          </h2>
          <p className="text-gray-600" data-id="element-463">
            Upcoming initiatives focused on healthcare excellence and community
            support.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-464">
          <FadeIn delay={0} data-id="element-465">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-466">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center" data-id="element-467">
                <Heart className="h-20 w-20 text-blue-600 opacity-20" data-id="element-468" />
              </div>
              <CardContent className="flex-1 p-6" data-id="element-469">
                <h3 className="text-xl font-serif font-bold mb-3 text-primary-900" data-id="element-470">
                  Non-Communicable Diseases
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-471">
                  Developing programs to address chronic conditions and promote
                  preventive healthcare in underserved communities.
                </p>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.1} data-id="element-472">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-473">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center" data-id="element-474">
                <Stethoscope className="h-20 w-20 text-red-600 opacity-20" data-id="element-475" />
              </div>
              <CardContent className="flex-1 p-6" data-id="element-476">
                <h3 className="text-xl font-serif font-bold mb-3 text-primary-900" data-id="element-477">
                  Trauma Medicine and Treatment
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-478">
                  Supporting emergency medical services and trauma care training
                  for healthcare professionals in Pakistan.
                </p>
              </CardContent>
            </Card>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-479">
            <Card className="h-full flex flex-col overflow-hidden hover:shadow-lg transition-shadow" data-id="element-480">
              <div className="h-48 overflow-hidden bg-gradient-to-br from-green-50 to-green-100 flex items-center justify-center" data-id="element-481">
                <BookOpen className="h-20 w-20 text-green-600 opacity-20" data-id="element-482" />
              </div>
              <CardContent className="flex-1 p-6" data-id="element-483">
                <h3 className="text-xl font-serif font-bold mb-3 text-primary-900" data-id="element-484">
                  Medical Education Fund
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed" data-id="element-485">
                  Continuing our scholarship program to support deserving
                  medical students from Khyber Pakhtunkhwa.
                </p>
              </CardContent>
            </Card>
          </FadeIn>
        </div>
      </Section>
    </>;
}